import { create } from 'zustand';
import { idb } from '../lib/idb';
import { api } from '../lib/api';
import { useAuthStore } from './auth';
import type { Circle, CircleMember } from '@nudge/shared';

interface CircleState {
  circle: Circle | null;
  members: CircleMember[];
  onlineMembers: Set<string>;
  bootstrapped: boolean;
  markBootstrapped: () => void;
  loadCircle: (id: string) => Promise<void>;
  createCircle: (name?: string) => Promise<Circle>;
  joinCircle: (inviteCode: string) => Promise<Circle>;
  leaveCircle: () => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
  regenInvite: () => Promise<string>;
  renameCircle: (name: string) => Promise<void>;
  transferOwnership: (userId: string) => Promise<void>;
  setOnline: (userIds: string[]) => void;
  markOnline: (userId: string) => void;
  markOffline: (userId: string) => void;
  setCircle: (circle: Circle | null) => void;
}

export const useCircleStore = create<CircleState>((set, get) => ({
  circle: null,
  members: [],
  onlineMembers: new Set(),
  bootstrapped: false,

  markBootstrapped() {
    if (!get().bootstrapped) set({ bootstrapped: true });
  },

  async loadCircle(id: string) {
    const { circle, members } = await api.getCircle(id);
    set({ circle, members });
    await idb.setCircleId(circle.id);
  },

  async createCircle(name?: string) {
    const { circle } = await api.createCircle({ name });
    await idb.setCircleId(circle.id);
    set({ circle });
    await get().loadCircle(circle.id);
    return circle;
  },

  async joinCircle(inviteCode: string) {
    const { circle } = await api.joinCircle({ invite_code: inviteCode });
    await idb.setCircleId(circle.id);
    set({ circle });
    await get().loadCircle(circle.id);
    return circle;
  },

  async leaveCircle() {
    const c = get().circle;
    if (!c) return;
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return;
    await api.removeMember(c.id, userId);
    await idb.setCircleId(null);
    set({ circle: null, members: [], onlineMembers: new Set() });
  },

  async removeMember(userId: string) {
    const c = get().circle;
    if (!c) return;
    await api.removeMember(c.id, userId);
    set({ members: get().members.filter((m) => m.user_id !== userId) });
  },

  async regenInvite() {
    const c = get().circle;
    if (!c) throw new Error('no_circle');
    const { invite_code } = await api.regenInvite(c.id);
    set({ circle: { ...c, invite_code } });
    return invite_code;
  },

  async renameCircle(name: string) {
    const c = get().circle;
    if (!c) throw new Error('no_circle');
    const { circle } = await api.renameCircle(c.id, name);
    set({ circle });
  },

  async transferOwnership(userId: string) {
    const c = get().circle;
    if (!c) throw new Error('no_circle');
    const callerId = useAuthStore.getState().user?.id;
    await api.transferOwnership(c.id, userId);
    set({
      members: get().members.map((m) => {
        if (m.user_id === userId) return { ...m, role: 'owner' };
        if (callerId && m.user_id === callerId) return { ...m, role: 'member' };
        return m;
      }),
    });
  },

  setOnline(userIds: string[]) {
    set({ onlineMembers: new Set(userIds) });
  },
  markOnline(userId: string) {
    const next = new Set(get().onlineMembers);
    next.add(userId);
    set({ onlineMembers: next });
  },
  markOffline(userId: string) {
    const next = new Set(get().onlineMembers);
    next.delete(userId);
    set({ onlineMembers: next });
  },
  setCircle(circle) {
    set({ circle });
  },
}));
